/**
 * Recording session scripts — style-tagged takes + verbatim templates.
 *
 * Script rules: children's-story prose only (the register we want cloned).
 * No placeholders, no stage directions, no all-caps, no tongue twisters,
 * no meta/promotional text. 90-140 words each.
 *
 * The server's /voice/session-config can override these (server-tunable copy);
 * these constants are the fallback and the source the server was seeded from.
 */

export type TakeStyle = 'calm' | 'warm' | 'excited' | 'whisper' | 'dialogue';

export interface TakePrompt {
  style: TakeStyle;
  title: string;
  text: string;
  minS: number;
  maxS: number;
  coaching: string; // one line shown above the script
}

export const TAKE_PROMPTS: TakePrompt[] = [
  {
    style: 'calm',
    title: 'A Quiet Beginning',
    coaching: 'Read slowly and gently, like the story is just beginning.',
    text:
      'The evening settled softly over the little valley, and one by one the ' +
      'lights in the village began to glow. Down by the river, the willow ' +
      'trees swayed in the warm night air, and the water hummed its old, slow ' +
      'song. In a small house at the edge of the meadow, a lamp burned low, ' +
      'and the smell of warm bread drifted from the kitchen. Somewhere far ' +
      'away, an owl called out once, then settled back into the quiet. The ' +
      'stars came out slowly, first one, then another, until the whole sky ' +
      'was full of tiny silver lanterns. It was the kind of night that made ' +
      'everything feel safe and soft and ready for sleep.',
    minS: 30,
    maxS: 90,
  },
  {
    style: 'calm',
    title: 'Winding Down',
    coaching: 'Keep a gentle pace, with long, soft sentences.',
    text:
      'The little boat rocked gently on the pond as the last light faded ' +
      'from the sky. A family of ducks paddled home through the reeds, ' +
      'leaving soft ripples behind them. On the hillside, the sheep had ' +
      'gathered under the old oak tree, woolly and warm against the cool ' +
      'evening. The moon rose slowly over the barn, round and golden, and ' +
      'painted long quiet shadows across the garden. Inside the farmhouse, ' +
      'the fire crackled low, and the old clock ticked its steady, sleepy ' +
      'rhythm. The cat curled up on the windowsill, tucked her nose beneath ' +
      'her tail, and let out one long, contented sigh. Everything, ' +
      'everywhere, was drifting gently toward morning.',
    minS: 30,
    maxS: 90,
  },
  {
    style: 'warm',
    title: 'Talking to Your Child',
    coaching: 'Imagine your child is right there. Speak to them, not at the page.',
    text:
      'Do you know what I love most about this time of day? It is when the ' +
      'whole world slows down, just for us. All the busy things are done ' +
      'now, and there is nothing left to do but be cozy together. You did so ' +
      'many wonderful things today, little one. You played and you laughed ' +
      'and you grew, just a tiny bit, the way you do every single day. And ' +
      'now the day is tucking itself in, just like you. The pillow is soft, ' +
      'the blanket is warm, and I am right here beside you. There is nowhere ' +
      'else I would rather be. Close your eyes now, and let the story carry ' +
      'you somewhere wonderful.',
    minS: 30,
    maxS: 90,
  },
  {
    style: 'excited',
    title: 'The Adventure Part',
    coaching: 'Add a little energy without shouting.',
    text:
      'And then, just as the clock struck nine, the whole treehouse began to ' +
      'sparkle! The floorboards glowed like embers, the windows filled with ' +
      'swirling stardust, and the ladder turned to shining silver. Milo ' +
      'grabbed his backpack and scrambled up as fast as his legs would ' +
      'carry him. The treehouse was lifting off! Up it sailed, over the ' +
      'rooftops, over the sleeping town, higher and higher into the ' +
      'glittering night. Below him, the streetlights looked like a string of ' +
      'tiny pearls. Ahead of him, a river of shooting stars poured across ' +
      'the sky. Where would it take him tonight? The cloud castles? The moon ' +
      'gardens? Milo laughed out loud. He could hardly wait to find out.',
    minS: 30,
    maxS: 90,
  },
  {
    style: 'whisper',
    title: 'Almost Asleep',
    coaching: 'Speak softly, as if sleep is close.',
    text:
      'Shhh. The whole house is sleeping now. The toys are resting in their ' +
      'basket, and even the goldfish has gone quiet in his bowl. Outside the ' +
      'window, the moon is keeping watch, soft and silver and slow. Can you ' +
      'hear it? That gentle hush, like the world pulling up its blanket. The ' +
      'little bear in the story found his cave, and curled up small, and ' +
      'closed his eyes. The wind sang to him, low and sweet, through the ' +
      'pine trees. Sleep now, said the wind. Sleep now, said the moon. And ' +
      'the little bear breathed in, and breathed out, and drifted away, ' +
      'soft as a snowflake, into the warmest, coziest dream.',
    minS: 30,
    maxS: 90,
  },
  {
    style: 'dialogue',
    title: 'Voices in the Story',
    coaching: 'Give each speaking part a slightly different voice.',
    text:
      'Pip the field mouse stood at the bottom of the great sunflower and ' +
      'looked up, and up, and up. At the very top, a sleepy bumblebee peered ' +
      'down at her. "Excuse me," called Pip, "but you are sitting on the ' +
      'very last sunflower seed of summer, and I need it for my winter ' +
      'pantry." The bumblebee yawned. "This seed?" he asked. "I thought it ' +
      'was a pillow. It is a very fine pillow." Pip climbed up, stem by ' +
      'stem, until they sat nose to nose. And when the moon rose over the ' +
      'field, the two of them shared the seed, half for the pantry, half ' +
      'for the pillow, and both of them agreed it was the best trade ever ' +
      'made.',
    minS: 30,
    maxS: 90,
  },
];

/**
 * Verbatim layer — recorded once, played back exactly as recorded, never
 * synthesized. The child hears the parent's REAL voice at the most
 * emotionally important moments.
 */
export interface VerbatimPrompt {
  key: 'intro' | 'goodnight' | 'name';
  title: string;
  /** Build display text; childName/parentName substituted at render time. */
  template: (childName: string, parentName: string) => string;
  coaching: string;
}

export const VERBATIM_PROMPTS: VerbatimPrompt[] = [
  {
    key: 'intro',
    title: 'Story Introduction',
    template: (child, parent) =>
      `Hi ${child}, it's ${parent}. This story is just for you.`,
    coaching: 'Say it as you normally would, warm and natural.',
  },
  {
    key: 'goodnight',
    title: 'Goodnight',
    template: (child) =>
      `Goodnight, ${child}. I love you. Sweet dreams.`,
    coaching: 'This recording plays at the end of every story.',
  },
  {
    key: 'name',
    title: 'Their Name',
    template: (child) => `${child}. ${child}.`,
    coaching: 'Just their name, said warmly, twice.',
  },
];

/** Mic-check configuration (thresholds mirrored by the server). */
export const MIC_CHECK = {
  roomToneS: 4,
  speechS: 5,
  sentence:
    'The moon rose slowly over the quiet garden, and the whole house settled down to sleep.',
  snrPassDb: 20,
  snrWarnDb: 12,   // 12-20 dB: warn with coaching; below 12: block
  peakMaxDb: -1,   // hotter than this: gain too hot, move back
};

export const CAPTURE_VERSION = 1;
